import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { merchantTypes, USER_DETAILS } from "./common/constants";
import Shimmer from "./components/Shimmer/Shimmer";
import { useDispatch } from "react-redux";
import { setIsMobile } from "./common/features/navigation/navigationActions";
import { useSelector } from "react-redux";
import TinyURL from "./pages/utilPages/tinyURL";
import { setUserDetails } from "./common/features/user/userActions";
import Loader from "./components/Loader";
import { get } from "react-hook-form";
import { getCookie } from "./helper/cookieHelper";
import Signupnew from "./components/Signupnew/Signupnew";
import { setSignupPopup as setSignupPopupAction } from "./common/features/navigation/navigationActions";
import CouponList from "./components/Coupons";

// Lazy load major pages/components
const RestaurantHome = React.lazy(() => import("./pages/MenuPage"));
const ItemDetails = React.lazy(() => import("./pages/menuDetails/index"));
const ItemDetails1 = React.lazy(() => import("./pages/menuDetails/menuDetails1"));
const MobileCart = React.lazy(() => import("./pages/mobileCart"));
const CheckOut = React.lazy(() => import("./pages/checkOut/CheckOut"));
const OrderTracking = React.lazy(() => import("./components/OrderTracking/OrderTracking"));
const ReceiptPage = React.lazy(() => import("./pages/PrintReceipt/PrintReceipt"));
const GuestLanding = React.lazy(() => import("./pages/guestLandingPage"));
const Home = React.lazy(() => import("./pages/home"));
const MapViewLocationPicker = React.lazy(() => import("./pages/locationPages/mapViewLocationPicker"));
const SearchLocation = React.lazy(() => import("./pages/locationPages/searchLocation"));
const LocationInformation = React.lazy(() => import("./pages/locationPages/locationInformation"));
const MerchantInfo = React.lazy(() => import("./pages/merchantInfo"));
const NotFound = React.lazy(() => import("./pages/utilPages/notFound"));
const CheckInSpinner = React.lazy(() => import("./pages/CheckIn/checkInSpinner"));
const CheckInConfirmation = React.lazy(() => import("./pages/CheckIn/checkInConfirmation"));
const ClosureMessage = React.lazy(() => import("./components/ClosureMessage/ClosureMessage"));
const RestaurantServiceStatus = React.lazy(() => import("./components/RestaurantServiceStatus/RestaurantServiceStatus"));

const CdOrderTracking = React.lazy(() => import("./pages/ContactlessDineIn/CdOrderTracking/CdOrderTracking"));
const CdFeedbacks = React.lazy(() => import("./pages/ContactlessDineIn/CdFeedbacks/CdFeedbacks"));
const DineInLogin = React.lazy(() => import("./pages/ContactlessDineIn"));
const BuzzerPicker = React.lazy(() => import("./components/BuzzerPicker/BuzzerPicker"));
const CdOrderTypePicker = React.lazy(() => import("./components/CdOrderTypePicker/CdOrderTypePicker"));

const AccountsPage = React.lazy(() => import("./pages/AccountsPage/AccountsPage"));
const TermsPolicies = React.lazy(() => import("./components/TermsPolicies/TermsPolicies"));
const TermsPolicy = React.lazy(() => import("./components/TermsPolicies/TermsPolicy/TermsPolicy"));
const CancellationPolicy = React.lazy(() => import("./components/TermsPolicies/Cancellation/Cancellation"));
const PrivacyPolicy = React.lazy(() => import("./components/TermsPolicies/Privacy/Privacy"));
const Faq = React.lazy(() => import("./components/FQA/FQA"));
const CardDetails = React.lazy(() => import("./components/CardDetails/CardDetails"));
const OrderCardList = React.lazy(() => import("./components/OrderCardList/OrderCardList"));
const OrderSummary = React.lazy(() => import("./components/OrderSummary/OrderSummary"));
const Favorites = React.lazy(() => import("./components/FavoriteItems/FavoriteItems"));
const DeliveryDetailsPage = React.lazy(() => import("./components/DeliveryDetailsPage/DeliveryDetailsPage"));
const HomePage = React.lazy(() => import("./components/HomePage/HomePage"));
const EventHomePage = React.lazy(() => import("./pages/eventHomePage/index"));
const MasterHomePage = React.lazy(() => import("./pages/masterHomePage"));
const RestaurantClosed = React.lazy(() => import('./components/restaurantClosed/index'));
const RestaurantInfoPage =React.lazy(() => import('./pages/RestaurantInfoContent/RestaurantInfoContent'));
const ExplorePage =React.lazy(() => import('./pages/ExploreMenu/ExploreMenu'));
const CheckInWelcome = React.lazy(() => import('./components/CheckInWelcome/CheckinWelcome'))
const ReservationRevamp = React.lazy(() => import('./reservation-revamp/ReservationRevamp'));
const CheckinRevamp = React.lazy(() => import('./reservation-revamp/CheckinRevamp'));
const PayLink = React.lazy(() => import('./pages/PayLink/PayLink'));

const OrderPathRedirect: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const strippedPath = window.location.pathname.replace(/^\/order/, '');
    navigate(strippedPath || '/landing', { replace: true });
  }, []);
  return <Loader />;
};

const LegacyReservationCheckinRedirect: React.FC<{ target: "ReservationRevamp" | "CheckinRevamp" }> = ({ target }) => {
  const { merchantType, merchantSlug, date } = useParams();
  const location = useLocation();
  const dateQuery = date ? `?date=${encodeURIComponent(date)}` : "";
  return (
    <Navigate
      to={`/${merchantType}/${merchantSlug}/${target}${dateQuery}`}
      replace
      state={{ fromLegacyPath: location.pathname }}
    />
  );
};
const EditProfile = React.lazy(() => import('./components/ProfileEdit/EditProfile'));
// const PayLink = React.lazy(() => import('./pages/PayLink/PayLink'));

const PayLinkPathCleanup: React.FC = () => {
  const { shortUrlKey } = useParams<{ shortUrlKey: string }>();
  return <Navigate to={shortUrlKey ? `/pay/${shortUrlKey}` : "/notFound"} replace />;
};

const AppRouter: React.FC = () => {
  const isMobile = useSelector((state: any) => state?.navigation?.isMobile);
  const restaurantDetails = useSelector((state: any) => state.restaurant.currentRestaurantDetail);
 

  const [disableReservation,setDisableReservation] = useState(false);

  useEffect(() => {
    if (!restaurantDetails?.disabledServices?.isReservation) {
      setDisableReservation(true);
    } else {
      setDisableReservation(false);
    }
  }, [restaurantDetails]);
  

  const dispatch = useDispatch()

  useEffect(() => {
    const handleResize = () => {
      dispatch(setIsMobile(window.innerWidth <= 768));
      setVh();
    };

    function setVh() {
      // window.innerHeight = "layout viewport" (always the large value, URL bar
      // excluded). On Safari iPhone, the URL bar's appearance/disappearance does
      // NOT change innerHeight, so --vh would remain stale.
      //
      // visualViewport.height = the ACTUAL visible area at any moment. It
      // correctly shrinks when Safari's URL bar is visible and grows when it
      // hides. This keeps --vh accurate and prevents vh-based elements from
      // overflowing or shifting when the URL bar toggles.
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
    }

    setVh();
    window.addEventListener('resize', handleResize);

    // window 'resize' does NOT fire when Safari's dynamic URL bar appears or
    // disappears — only visualViewport 'resize' does. Without this listener,
    // --vh stays stale for the entire session on iPhone Safari.
    window.visualViewport?.addEventListener('resize', setVh);

    const userDetails = getCookie(USER_DETAILS);
    if (userDetails?.customerId) {
      dispatch(setUserDetails(userDetails));
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', setVh);
    };
  }, []);
  const baseMenuRoutes=["/:merchantType/:merchantSlug/menu/:menuType","/:merchantType/:merchantSlug/menu/:menuType/event/:eventDate","/:merchantType/:merchantSlug/menu/:menuType/e/:eventDate","/:merchantType/:merchantSlug/table/:tableId/menu/:menuType","/:merchantType/:merchantSlug/table/:tableId/order/:orderId/menu/:menuType" ]
  // Same routes prefixed with /order — URL is preserved (no redirect) for SEO/Cloudflare Worker URLs
  const orderBaseMenuRoutes = baseMenuRoutes.map(r => `/order${r}`);
  return (
    <Router>
      <Suspense fallback={<Loader/> }>
      
        <RestaurantServiceStatus/>
         <ClosureMessage />
        <Routes>
          {/* Previous routing logic */}
          <Route path="/" element={<Home restaurantViewType="default" />} />
          <Route path="/order" element={<Home restaurantViewType="default" />} />
          <Route path="/homepage" element={<HomePage />} />
          <Route
            path="/restaurants/city/:cityName"
            element={<Home restaurantViewType="cityName" />}
          />
          <Route
            path="/restaurants/location/:lat/:lng"
            element={<Home restaurantViewType="location" />}
          />
          <Route path="/landing" element={<GuestLanding />} />
          <Route path="/location/pick" element={<MapViewLocationPicker />} />
          <Route path="/location/search" element={<SearchLocation />} />
          <Route path="/location/info" element={<LocationInformation />} />
          {/* <Route path="/qrscan" element={<QrScan/>} /> */}
          <Route path="/notFound" element={<NotFound />} />
          <Route
            path="/:merchantType/:merchantSlug/table/:tableId/menu/:menuType/contactlessdinein/landing"
            element={<DineInLogin />}
          />
           <Route
            path="/:merchantType/:merchantSlug/table/:tableId/menu/:menuType/contactlessdinein/Buzzer"
            element={<BuzzerPicker />}
          />
          <Route
            path="/:merchantType/:merchantSlug/table/:tableId/menu/:menuType/contactlessdinein/orderType"
            element={<CdOrderTypePicker />}
          />
          {baseMenuRoutes?.map((route:string)=>    <Route
            path={route}
            element={<RestaurantHome />}
          />)}
                {baseMenuRoutes?.map((route:string)=>    <Route
            path={`${route}/restaurantinfo`}
            element={<RestaurantInfoPage />}
          />)}
                {baseMenuRoutes?.map((route:string)=>    <Route
            path={`${route}/exploremenu`}
            element={<ExplorePage />}
          />)}

          {/* /order-prefixed menu routes — URL is preserved as-is (no redirect) */}
          {orderBaseMenuRoutes?.map((route:string)=>    <Route
            path={route}
            element={<RestaurantHome />}
          />)}
          {orderBaseMenuRoutes?.map((route:string)=>    <Route
            path={`${route}/restaurantinfo`}
            element={<RestaurantInfoPage />}
          />)}
          {orderBaseMenuRoutes?.map((route:string)=>    <Route
            path={`${route}/exploremenu`}
            element={<ExplorePage />}
          />)}
        
          <Route
            path="/:merchantType/:merchantSlug/menu"
            element={<RestaurantHome />}
          />
          <Route
            path="/:merchantType/:merchantSlug/account"
            element={<AccountsPage />}
          />
          <Route
            path="/:merchantType/:merchantSlug/account/:name"
            element={<AccountsPage />}
          />
           <Route
            path="/:merchantType/:merchantSlug/account/:name/:orderId"
            element={<AccountsPage />}
          />

          {/* <Route
          path="/:merchantType/:merchantSlug/account/:name/:policyname"
          element={<AccountsPage />}
        /> */}

          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/Profile"
              element={<AccountsPage />}
            />
          )}


          <Route
            path="/:merchantType/:merchantSlug/e/:eventSlug"
            element={<EventHomePage />}
          />

          <Route
            path="/:merchantType/:merchantSlug/details"
            element={<MasterHomePage />}
          />


          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/Favorites"
              element={<Favorites /> }
            />
          )}
          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/EditProfile"
              element={<EditProfile />}
            />
          )}
          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/DeliveryAddress"
              element={<DeliveryDetailsPage /> }
            />
          )}

          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/policies"
              element={<TermsPolicies />}
            />
          )}

          {/* Add desktop route for Policies page. :policyname is optional so the
              in-page tab switch (Terms of use / Cancellation & refund policy /
              Privacy Policy) can push a matching URL without a separate route. */}
          <Route
            path="/:merchantType/:merchantSlug/account/Policies/:policyname?"
            element={<AccountsPage />}
          />

          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/policies/terms"
              element={ <TermsPolicy /> }
            />
          )}

          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/policies/CancellationPolicy"
              element={<CancellationPolicy /> }
            />
          )}
          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/policies/PrivacyPolicy"
              element={ <PrivacyPolicy /> }
            />
          )}
          { (
            <Route
              path="/:merchantType/:merchantSlug/PrivacyPolicy"
              element={isMobile ? <PrivacyPolicy /> : <AccountsPage />}
            />
          )}
          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/FAQ"
              element={ <Faq /> }
            />
          )}

          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/Cards"
              element={ <CardDetails /> }
            />
          )}

          {isMobile && (
            <Route
              path="/:merchantType/:merchantSlug/account/MyOrders"
              element={ <OrderCardList /> }
            />
          )}

          <Route
            path="/:merchantType/:merchantSlug/account/MyOrders/OrderSummary/:orderId"
            element={<OrderSummary />}
          />


          
          <Route
            path="/:merchantType/:merchantSlug/table/:tableId"
            element={<MerchantInfo />}
          />
          <Route path="/:merchantType/:merchantSlug" element={<HomePage />} />
          {/* TODO:add desktop page MerchantInfoDesktop */}
          <Route
            path="/:merchantType/:merchantSlug/campaign/:campaignId"
            element={<MerchantInfo />}
          />
           <Route
            path="/:merchantType/:merchantSlug/exploremenu"
            element={<ExplorePage />}
          />
           <Route
            path="/:merchantType/:merchantSlug/restaurantinfo"
            element={<RestaurantInfoPage />}
          />
          <Route
            path="/:merchantType/:merchantSlug/checkin"
            element={<LegacyReservationCheckinRedirect target="CheckinRevamp" />}
          />
          <Route
            path="/:merchantType/:merchantSlug/checkin/table/:tableId/details"
            element={<CheckInSpinner />}
          />
           <Route
            path="/:merchantType/:merchantSlug/menu/:menuType/checkIn/table/:tableId"
            element={<CheckInWelcome />}
          />
          <Route
            path="/:merchantType/:merchantSlug/checkIn/table/:tableId"
            element={<CheckInWelcome />}
          />
          <Route
            path="/:merchantType/:merchantSlug/reservation/:reservationId"
            element={<CheckInConfirmation />}
          />
          <Route
            path="/:merchantType/:merchantSlug/checkIn/table/:tableId"
            element={<CheckInWelcome />}
          />
          <Route
            path="/:merchantType/:merchantSlug/reservation/status/:reservationId"
            element={<CheckInConfirmation />}
          />
           
          <Route
  path="/:merchantType/:merchantSlug/Reservation"
  element={<LegacyReservationCheckinRedirect target="ReservationRevamp" />}
/>
          <Route
            path="/:merchantType/:merchantSlug/Reservation/:date"
            element={<LegacyReservationCheckinRedirect target="ReservationRevamp" />}
          />
          <Route
            path="/:merchantType/:merchantSlug/ReservationRevamp/status/:reservationId"
            element={<ReservationRevamp />}
          />
          <Route
            path="/:merchantType/:merchantSlug/CheckinRevamp/status/:reservationId"
            element={<CheckinRevamp />}
          />
          <Route
            path="/:merchantType/:merchantSlug/ReservationRevamp"
            element={<ReservationRevamp />}
          />
          <Route
            path="/:merchantType/:merchantSlug/CheckinRevamp"
            element={<CheckinRevamp />}
          />
          <Route
          path="/:merchantType/:merchantSlug/e/:eventSlug"
          element={<EventHomePage />}
        />
          {/* Test Routes */}
          <Route
            path="/:merchantType/:merchantSlug/menu/:menuType/:categoryId/:itemName"
            element={restaurantDetails?.isProductCatalogV2Enabled ? <ItemDetails /> : <ItemDetails1 /> }
          />
          <Route
            path="/:merchantType/:merchantSlug/menu/:menuType/:itemName"
            element={restaurantDetails?.isProductCatalogV2Enabled ? <ItemDetails /> : <ItemDetails1 /> }
          />
          <Route
            path="/:merchantType/:merchantSlug/table/:tableId/menu/:menuType/:categoryId/:itemName"
            element={restaurantDetails?.isProductCatalogV2Enabled ? <ItemDetails /> : <ItemDetails1 /> }
          />
           <Route
            path="/:merchantType/:merchantSlug/table/:tableId/menu/:menuType/:itemName"
            element={restaurantDetails?.isProductCatalogV2Enabled ? <ItemDetails /> : <ItemDetails1 /> }
          />
          <Route path="/:merchantType/:merchantSlug/cart" element={<MobileCart />} />
          <Route path="/:merchantType/:merchantSlug/checkOut" element={<CheckOut />} />
          <Route path="/Shimmer" element={<Shimmer />} />

          {/* Order Tracking & Routes ----------------*/}

           {/* for instore,pickup,delivery orders  --- */}
          <Route path="/:merchantType/:merchantSlug/tracker/:orderId" element={<OrderTracking />} />
          <Route path="/:merchantType/:merchantSlug/tracker/:orderId/e/splitId/:splitId" element={<OrderTracking />} />
          <Route path="/:merchantType/:merchantSlug/tracker/:orderId/e/transactionId/:transactionId" element={<OrderTracking />} />
           {/*  --- for instore,pickup,delivery orders  */}


            {/* cd order statua (or) dine in track logoUrl  ---*/}
          <Route path="/:merchantType/:restaurantSlug/table/:tableId/order/:orderId" element={<CdOrderTracking />} />
          <Route path="/:merchantType/:restaurantSlug/table/:tableId/order/:orderId/e/splitId/:splitId"
           element={<CdOrderTracking />} />
          <Route path="/:merchantType/:restaurantSlug/table/:tableId/order/:orderId/e/transactionId/:transactionId"
           element={<CdOrderTracking />} />
           {/*  --- cd order statua (or) dine in track logoUrl */}

            {/*  ---------------- Order Tracking & Routes*/}


         
          <Route path="/tracker/:orderId/orderSummary" element={<OrderTracking />} />
          <Route path="/receipt/:orderId" element={<ReceiptPage />} />

          <Route
            path="/table/:tableId/menu/:menuType/itemDetails"
            element={restaurantDetails?.isProductCatalogV2Enabled ? <ItemDetails /> : <ItemDetails1 /> }
          />
          <Route
            path="/:merchantType/:merchantSlug/table/:tableId/menu/:menuType"
            element={<RestaurantHome />}
          />
          <Route
            path="/:merchantType/:merchantSlug/table/:tableId/menu/:menuType/contactlessdinein/cart"
            element={<MobileCart />}
          />
          <Route
            path="/:merchantType/:restaurantSlug/table/:tableId/order/:orderId/placeOrder/payment"
            element={<CheckOut />}
          />
          <Route path="/order/:orderId/CD/feedbacks" element={<CdFeedbacks />} />

          {/* receipt links */}

          <Route
            path="/:merchantType/:restaurantSlug/receipt/table/:tableId/order/:orderId"
            element={<ReceiptPage />}
          />
          <Route
            path="/:merchantType/:restaurantSlug/receipt/table/:tableId/order/:orderId/e/splitId/:splitId"
            element={<ReceiptPage />}
          />
          <Route
            path="/:merchantType/:restaurantSlug/receipt/table/:tableId/order/:orderId/e/splitId/:splitId"
            element={<ReceiptPage />}
          />

          <Route
            path="/:merchantType/:merchantSlug/order/:orderId/orderSummary"
            element={<ReceiptPage />}
          />

          <Route
            path="/order/:orderId/orderSummary"
            element={<ReceiptPage />}
          />
          <Route
            path="/:merchantType/:merchantSlug/receipt/order/:orderId/orderSummary"
            element={<ReceiptPage />}
          />
          <Route
            path="/:merchantType/:merchantSlug/receipt/table/:tableId/order/:orderId/orderSummary"
            element={<ReceiptPage />}
          />

          <Route
            path="/:merchantType/:merchantSlug/receipt/order/:orderId/e/splitId/:splitId/orderSummary"
            element={<ReceiptPage />}
          />

          <Route
            path="/:merchantType/:merchantSlug/receipt/table/:tableId/order/:orderId/e/splitId/:splitId/orderSummary"
            element={<ReceiptPage />}
          />
          {/* 
         <RouteValidator
          exact
          path="/:merchantType/:merchantSlug/receipt/order/:orderId/e/transactionId/:transactionId/orderSummary"
          component={OrderSummary}
        /> */}


          <Route
            path="/:merchantType/:merchantSlug/receipt/table/:tableId/order/:orderId/e/transactionId/:transactionId/orderSummary"
            element={<ReceiptPage />}
          />
          <Route
            path="/:merchantType/:merchantSlug/receipt/order/:orderId/e/transactionId/:transactionId/orderSummary"
            element={<ReceiptPage />} />

          <Route
            path="/receipt/order/:orderId/e/transactionId/:transactionId/orderSummary"
            element={ <ReceiptPage />} />

          <Route
            path="/receipt/table/:tableId/order/:orderId/e/transactionId/:transactionId/orderSummary"
            element={<ReceiptPage />} />
          <Route
            path="/receipt/order/:orderId/e/splitId/:splitId/orderSummary"
            element={<ReceiptPage />}
          />
          <Route

            path="/receipt/table/:tableId/order/:orderId/e/splitId/:splitId/orderSummary"
            element={<ReceiptPage />}
          />
          <Route

            path="/receipt/table/:tableId/order/:orderId/orderSummary"
            element={<ReceiptPage />}
          />
          <Route

            path="/receipt/order/:orderId/orderSummary"
            element={<ReceiptPage />}
          />
         
          {/* order tracking page */}
          <Route
            path="/:merchantType/:merchantSlug/order/:orderId"
            element={isMobile ? <OrderTracking /> : <OrderTracking />}
          />
          <Route

            path="/checkIn/:merchantSlug/closed"
            element={<RestaurantClosed />}
          />
          <Route
            path="/reservation/:merchantSlug/closed"
            element={<RestaurantClosed />}
          />
          <Route
            path="/:merchantType/:merchantSlug/tableside/checkIn/:tableId"
            element={<CheckInWelcome />}
          />

          {/* Coupons */}
          <Route
            path="/:merchantType/:merchantSlug/coupons"
            element={<CouponList />}
          />
          <Route
            path="/:merchantType/:merchantSlug/table/:tableId/contactlessdinein/coupons"
            element={<CouponList />}
          />

          {/* Pay Link — short-url order payment page */}
          <Route path="/pay/:shortUrlKey" element={<PayLink />} />

          {/* Catch-all for /order/* — strips /order prefix and redirects to the equivalent path */}
          <Route path="/order/*" element={<OrderPathRedirect />} />
          {/* PayLink's public short URL. */}
          <Route path="/pay/:shortUrlKey" element={<PayLink />} />
          {/* Normalize an accidentally appended menu path back to the PayLink URL. */}
          <Route path="/pay/:shortUrlKey/*" element={<PayLinkPathCleanup />} />

          <Route path="/:tinyURLId" element={<TinyURL />} />
          <Route path="*" element={<Navigate to="/notFound" replace />} />
          
        </Routes>
      </Suspense>
      <GlobalSignupHandler />
    </Router>
  );
};

// Global component to handle Signupnew on all pages (inside Router context)
const GlobalSignupHandler: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const showSignupPopup = useSelector((state: any) => state?.navigation?.showSignupPopup);
  const isMobile = useSelector((state: any) => state?.navigation?.isMobile);
  const restaurantDetails = useSelector((state: any) => state.restaurant.currentRestaurantDetail);
  const pathname = location.pathname.toLowerCase();
  const isContactlessDineInRoute = pathname.includes('/contactlessdinein');
  const isContactlessDineInTableRoute =
    /^\/[^/]+\/[^/]+\/table\/[^/]+\/(?:menu|order)\//.test(pathname) &&
    [2, 3].includes(Number(restaurantDetails?.dining));
  // Terms and Policies must stay readable by anonymous visitors — a guest
  // checking the box on the sign-in/checkout consent line follows the
  // "Click here to view Privacy Policy" link before they've ever logged in,
  // so a 401/403 from an account-only background request on this page (e.g.
  // AccountsPage's loyalty/delivery-address fetches) must not interrupt them
  // with the sign-in modal.
  const isPoliciesRoute = pathname.includes('/account/policies');
  const shouldSuppressSignupPopup =
    isContactlessDineInRoute || isContactlessDineInTableRoute || isPoliciesRoute;

  // CD is a guest flow. A 401/403 from one of its background requests must
  // not turn into the app-wide sign-in modal. Clear a pending global trigger
  // immediately so it cannot appear after navigation to another CD screen.
  useEffect(() => {
    if (shouldSuppressSignupPopup && showSignupPopup) {
      dispatch(setSignupPopupAction(false));
    }
  }, [shouldSuppressSignupPopup, showSignupPopup, dispatch]);

  // Debug: Log when state changes
  // useEffect(() => {
  //   console.log('GlobalSignupHandler: showSignupPopup state changed to:', showSignupPopup);
  //   if (showSignupPopup) {
  //     console.log('GlobalSignupHandler: showSignupPopup is true, showing Signupnew');
  //   }
  // }, [showSignupPopup]);

  // Only render when state is true to avoid unnecessary renders
  if (!showSignupPopup || shouldSuppressSignupPopup) {
    return null;
  }

  return (
    <Signupnew
      isOpen={showSignupPopup}
      signInPopupDesktop={!isMobile}
      onClose={() => {
        // console.log('GlobalSignupHandler: Closing Signupnew');
        dispatch(setSignupPopupAction(false));
      }}
    />
  );
};

export default AppRouter;
